import { useState, useMemo, useCallback, useEffect } from 'react';
import { DEFAULTS } from '../constants';
import { useLazyFetchWeeklyGLPostingSublocationsQuery } from '../../../services/WeeklyGLPosting/WeeklyGLPostingService';

const toBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true' || value.trim() === '1';
  return false;
};

const normalizeCurrencyIds = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [values])
        .map((value) => String(value ?? '').trim())
        .filter(Boolean)
        .filter((value) => /^\d+$/.test(value)),
    ),
  );

export const useSublocationFetch = ({
  selectedX3ProductName,
  selectedCurrencies = DEFAULTS.EMPTY_ARRAY,
  onApiError,
}) => {
  const [apiSublocations, setApiSublocations] = useState(DEFAULTS.EMPTY_ARRAY);
  const [isLoadingSublocations, setIsLoadingSublocations] = useState(false);
  const [sublocationSearchTerm, setSublocationSearchTerm] = useState(DEFAULTS.EMPTY_STRING);
  const [fetchSublocations] = useLazyFetchWeeklyGLPostingSublocationsQuery();
  const normalizedSelectedCurrencyIds = useMemo(() => normalizeCurrencyIds(selectedCurrencies), [selectedCurrencies]);

  useEffect(() => {
    if (!selectedX3ProductName || normalizedSelectedCurrencyIds.length === 0) {
      setApiSublocations(DEFAULTS.EMPTY_ARRAY);
      setIsLoadingSublocations(false);
      return;
    }

    let isCancelled = false;

    const loadSublocations = async () => {
      try {
        setIsLoadingSublocations(true);
        const response = await fetchSublocations({
          x3ProductName: selectedX3ProductName,
          currencyIds: normalizedSelectedCurrencyIds,
        }).unwrap();
        if (isCancelled) return;

        const sublocations = Array.isArray(response?.sublocations) ? response.sublocations : [];
        const normalized = sublocations
          .map((item) => {
            const normalizedId = String(item?.id ?? '');

            return {
              id: normalizedId,
              name: item?.name ?? DEFAULTS.EMPTY_STRING,
              isPosted: toBoolean(item?.isPosted),
              lastPosting: item?.lastPosting ?? null,
            };
          })
          .filter((item) => item.id && item.name);

        setApiSublocations(normalized);
      } catch (error) {
        if (!isCancelled) {
          setApiSublocations(DEFAULTS.EMPTY_ARRAY);
          onApiError?.(error);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingSublocations(false);
        }
      }
    };

    loadSublocations();

    return () => {
      isCancelled = true;
    };
  }, [selectedX3ProductName, normalizedSelectedCurrencyIds, fetchSublocations, onApiError]);

  const sortedSublocations = useMemo(() => {
    if (!Array.isArray(apiSublocations) || apiSublocations.length === 0) return [];
    return [...apiSublocations].sort((a, b) => {
      const aPosted = a?.isPosted ? 1 : 0;
      const bPosted = b?.isPosted ? 1 : 0;
      if (aPosted !== bPosted) return aPosted - bPosted;
      const aName = (a?.name ?? '').toString();
      const bName = (b?.name ?? '').toString();
      return aName.localeCompare(bName, 'en', { sensitivity: 'base' });
    });
  }, [apiSublocations]);

  const filteredSublocations = useMemo(() => {
    if (sortedSublocations.length === 0) return [];
    if (!sublocationSearchTerm) return sortedSublocations;
    const searchLower = sublocationSearchTerm.toLowerCase();
    return sortedSublocations.filter((sub) => (sub?.name ?? '').toLowerCase().includes(searchLower));
  }, [sortedSublocations, sublocationSearchTerm]);

  const hasSublocations = apiSublocations.length > 0;

  const handleSublocationSearch = useCallback((searchTerm) => {
    setSublocationSearchTerm(searchTerm ?? DEFAULTS.EMPTY_STRING);
  }, []);

  const resetSublocations = useCallback(() => {
    setApiSublocations(DEFAULTS.EMPTY_ARRAY);
    setSublocationSearchTerm(DEFAULTS.EMPTY_STRING);
  }, []);

  return {
    apiSublocations,
    sortedSublocations,
    filteredSublocations,
    sublocationSearchTerm,
    isLoadingSublocations,
    hasSublocations,
    onSearch: handleSublocationSearch,
    resetSublocations,
  };
};
